import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Search } from 'lucide-react';
import { NAV } from '@/constants/testIds';
import Logo from './Logo';

const links = [
  { label: 'Collections', hash: 'collections' },
  { label: 'Why Us',      hash: 'why-us' },
  { label: 'About',       hash: 'about' },
  { label: 'Gallery',     hash: 'gallery' },
  { label: 'Contact',     hash: 'contact' },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /**
   * Universal in-page/cross-page navigation for the nav links.
   * - On home page: close the mobile menu first, wait for the AnimatePresence
   *   exit animation to finish (so the page height is stable), then perform a
   *   header-offset scroll. This prevents the scroll from being interrupted by
   *   the collapsing menu, which was causing "menu closes but nothing scrolls"
   *   on mobile.
   * - On other routes: push `/#hash` via React Router; Home.jsx picks it up.
   */
  const HEADER_OFFSET = 80; // matches h-20 on the fixed header
  const scrollToId = (hash) => {
    const el = document.getElementById(hash);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const goTo = (hash) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const wasOpen = open;
    if (pathname === '/') {
      window.history.replaceState(null, '', `/#${hash}`);
      if (wasOpen) {
        // Close menu first, then wait for the 0.4s exit animation to finish
        // (plus a small buffer) before scrolling so layout is stable.
        setOpen(false);
        setTimeout(() => scrollToId(hash), 450);
      } else {
        scrollToId(hash);
      }
    } else {
      setOpen(false);
      navigate(`/#${hash}`);
    }
  };

  return (
    <header
      data-testid={NAV.root}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-ivory/90 backdrop-blur-xl border-b border-brandborder/60 shadow-luxe'
          : 'bg-transparent'
      }`}
    >
      <div className="container-luxe flex items-center justify-between h-20">
        <a
          href="#top"
          data-testid={NAV.logo}
          className="flex items-center gap-3 group"
          onClick={() => setOpen(false)}
        >
          <Logo size="md" />
          <div className="leading-tight">
            <div className="font-display text-xl text-charcoal tracking-tight">Trendy Closet</div>
            <div className="overline text-[10px]">Indian Luxe · Morrisville</div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.hash}
              href={`/#${l.hash}`}
              onClick={goTo(l.hash)}
              data-testid={NAV.link(l.label.toLowerCase().replace(/\s+/g, '-'))}
              className="text-sm font-medium text-charcoal/80 hover:text-maroon transition-colors duration-300 relative after:content-[''] after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-gold hover:after:w-full after:transition-all after:duration-500"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/search"
            data-testid="nav-search"
            aria-label="Search"
            className="w-10 h-10 border border-brandborder hover:border-maroon text-charcoal/70 hover:text-maroon flex items-center justify-center transition-colors"
          >
            <Search className="w-4 h-4" />
          </Link>
          <a
            href="tel:+19843779835"
            className="flex items-center gap-2 text-sm font-medium text-charcoal/80 hover:text-maroon transition"
          >
            <Phone className="w-4 h-4" /> 984-377-9835
          </a>
          <a
            href="/#appointment"
            onClick={goTo('appointment')}
            data-testid={NAV.cta}
            className="px-6 py-3 bg-burgundy hover:bg-maroon text-ivory text-xs uppercase tracking-[0.18em] transition-colors duration-500"
          >
            Book Appointment
          </a>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <Link
            to="/search"
            data-testid="nav-search-mobile"
            aria-label="Search"
            className="p-2 text-charcoal/80 hover:text-maroon"
          >
            <Search className="w-5 h-5" />
          </Link>
          <button
            data-testid={NAV.mobileToggle}
            onClick={() => setOpen((v) => !v)}
            className="p-2 text-charcoal"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={NAV.mobileMenu}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4 }}
            className="lg:hidden bg-ivory/95 backdrop-blur-xl border-t border-brandborder/60 overflow-hidden"
          >
            <div className="container-luxe py-6 flex flex-col gap-4">
              {links.map((l) => (
                <a
                  key={l.hash}
                  href={`/#${l.hash}`}
                  onClick={goTo(l.hash)}
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="text-base font-medium text-charcoal/90 hover:text-maroon py-2"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="/#appointment"
                onClick={goTo('appointment')}
                data-testid="nav-mobile-cta"
                className="mt-2 px-6 py-3 bg-burgundy text-ivory text-xs uppercase tracking-[0.18em] text-center"
              >
                Book Appointment
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
